import React from 'react';

import Permissions from 'react-redux-permissions';
import { type RouteComponentProps, Redirect, Route, Switch, withRouter } from 'react-router';

import LazyComponent from '@fd/ui/LazyComponent';

import { lazyWithRetry } from '../../helpers/utilities';
import ErrorBoundary from '../../layouts/Portal/ErrorBoundary';
import { PortalFeatureFlags } from '../../services/featureFlags/portalFeatureFlags';
import { useBooleanFeatureFlagResolution } from '../../services/featureFlags/useBooleanFeatureFlagResolution';
import KiosksRoutes from '../Kiosks/Kiosks.routes';
import SalesChannelRoutes from '../RMS/SalesChannels/SalesChannelRoutes';
import { RMSModules } from '../RMS/utils/rmsModules';
import { RMSSubscriptions } from '../RMS/utils/rmsSubscriptions';
import IntegrationsPage from './Integrations/IntegrationsPage';
import MarketPlacesPage from './MarketPlaces/MarketPlacesPage';
import { MobileApps, MobileAppsConfiguration } from './MobileApps/pages';
import { getSalesChannelPermissions } from './permissions';
import PhoneAgentRoutes from './PhoneAgent.routes';
import PointOfSaleRoutes from './pos.routes';
import WebsiteRoutes from './website.routes';

const RMSPage = lazyWithRetry(() => import('../RMS/components/RMSPage'));
const OrderingPlatformPage = lazyWithRetry(() => import('./OrderingPlatforms/OrderingPlatforms'));

type RouteParams = {
  appId?: string;
};

type Props = RouteComponentProps<RouteParams>;
const SalesChannelsRoutes: React.FC<React.PropsWithChildren<Props>> = (props: Props) => {
  const {
    match: { path, params },
  } = props;
  const useAuthZForGetApps = useBooleanFeatureFlagResolution(
    PortalFeatureFlags.backend_auth_useAuthZForGetApps,
    false
  );
  const salesChannelPermissions = getSalesChannelPermissions(
    useAuthZForGetApps.status === 'resolved' && useAuthZForGetApps.value
  );

  return (
    <ErrorBoundary identifier="sales-channels">
      <Switch>
        <>
          <Route
            exact
            path={`${path}/website-settings`}
            render={() => <Redirect to={`/${params?.appId}/sales-channels/website`} />}
          />
          <Permissions allowed={salesChannelPermissions}>
            <Route exact path={`${path}/mobile-apps`} component={MobileApps} />
            <Route
              exact
              path={`${path}/mobile-apps/configuration`}
              component={MobileAppsConfiguration}
            />
            <WebsiteRoutes path={path} />
            <PhoneAgentRoutes path={path} />
          </Permissions>
          <Permissions allowed={salesChannelPermissions}>
            <PointOfSaleRoutes path={path} appId={params?.appId} />
            <Route
              path={`${path}/:type(point-of-sale|website|kiosk|marketplaces|mobile-apps|phone-agent)`}
              component={SalesChannelRoutes}
            />
            <Route exact path={`${path}/marketplaces`} component={IntegrationsPage} />
            <Route
              path={`${path}/marketplaces/v2`}
              render={() => (
                <LazyComponent>
                  <OrderingPlatformPage />
                </LazyComponent>
              )}
            />
            <Route exact path={`${path}/marketplaces/v1`} component={MarketPlacesPage} />
            <Route
              exact
              path={`${path}/:type(point-of-sale|website|kiosk|mobile-apps)/devices`}
              render={() => (
                <LazyComponent>
                  <RMSPage
                    toParent="./"
                    subscription={RMSSubscriptions.BASE}
                    title={'Devices'}
                    module={RMSModules.STATIONS}
                    url={`configurations/system/${RMSModules.STATIONS}`}
                    includePropertyIdInUrl
                  />
                </LazyComponent>
              )}
            />
          </Permissions>
          <Permissions allowed={salesChannelPermissions}>
            <KiosksRoutes path={path} />
          </Permissions>
        </>
      </Switch>
    </ErrorBoundary>
  );
};

export default withRouter(SalesChannelsRoutes);
